/**
 * Engagement-level Review Points dashboard.
 *
 * Renders the six summary cards (Total / Pending / In Progress / Resolved
 * / Cleared / Overdue), a filter bar, and the full table with a
 * click-to-open detail drawer that reuses ReviewPointsDrawer.
 */
import React, { useEffect, useMemo, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import { useResizableColumns, ResizableTh, ResetCaption } from "@/components/ui/resizable-table";
import { useEngagement } from "@/contexts/EngagementContext";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Search, Download, ExternalLink } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ReviewCountsProvider, ReviewPointsDrawer,
} from "@/components/reviewpoints/ReviewPointsPanel";
import { API_URL } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate } from "@/lib/dt";

const STATUS_COLOR = {
  "Pending": "border-red-300 bg-red-50 text-red-800",
  "In Progress": "border-orange-300 bg-orange-50 text-orange-800",
  "Resolved": "border-blue-300 bg-blue-50 text-blue-800",
  "Cleared": "border-emerald-300 bg-emerald-50 text-emerald-800",
};

export default function ReviewPointsPage() {
  const { t, dict } = useI18n();
  const { currentEngId } = useEngagement();
  const navigate = useNavigate();
  const TARGET_LABEL = dict.reviewPointsPage?.targets || {};
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [role, setRole] = useState("all");
  const [priority, setPriority] = useState("all");
  const [targetType, setTargetType] = useState("all");
  const [q, setQ] = useState("");
  const [openRow, setOpenRow] = useState(null);
  const rpCols = useResizableColumns({
    storageKey: "review-points/main",
    defaults: [280, 110, 220, 130, 100, 100, 90, 100, 100, 60],
  });

  const load = async () => {
    if (!currentEngId) return;
    setLoading(true);
    try {
      const r = await api.get(`/engagements/${currentEngId}/review-dashboard`);
      setSummary(r.data.summary || {});
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(toErrMsg(e, t("reviewPointsPage.toastLoadFail")));
    } finally { setLoading(false); }
  };
  const loadOnChange = useEffectEvent(load);
  useEffect(() => { loadOnChange(); }, [currentEngId, loadOnChange]);

  const raisedBy = new URLSearchParams(window.location.search).get("raised_by") || ""; // Round 27 · X1 — EQ reviewer's points
  const filtered = useMemo(() => {
    const qq = (q || "").trim().toLowerCase();
    return rows.filter((r) => {
      if (raisedBy && r.raised_by_user_id !== raisedBy) return false;
      if (status !== "all" && r.status !== status) return false;
      if (role !== "all" && r.raised_by_role !== role) return false;
      if (priority !== "all" && r.priority !== priority) return false;
      if (targetType !== "all" && r.target_type !== targetType) return false;
      if (qq && !(r.review_point_text || "").toLowerCase().includes(qq)) return false;
      return true;
    });
  }, [rows, status, role, priority, targetType, q, raisedBy]);

  const uniqueRoles = useMemo(() => {
    const set = new Set(rows.map((r) => r.raised_by_role).filter(Boolean));
    return Array.from(set);
  }, [rows]);

  // ---- Export handler (CSV / XLSX / PDF) ----
  // Exports mirror the on-screen filter so partners download exactly
  // what they see. Uses fetch + blob so the download-attribute filename
  // survives (window.open would drop it because of the Bearer header).
  const doExport = async (fmt) => {
    if (!currentEngId) return;
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (role !== "all") params.set("role", role);
    if (priority !== "all") params.set("priority", priority);
    if (targetType !== "all") params.set("target_type", targetType);
    if (q?.trim()) params.set("q", q.trim());
    const url = `${API_URL}/engagements/${currentEngId}/review-dashboard/export.${fmt}${params.toString() ? `?${params}` : ""}`;
    const tok = localStorage.getItem("af_token");
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) {
        toast.error(t("reviewPointsPage.toastExportStatusFail").replace("{status}", res.status));
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const fname = m ? m[1] : `review_points.${fmt}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(t("reviewPointsPage.toastExported").replace("{fname}", fname));
    } catch (e) {
      toast.error(toErrMsg(e, t("reviewPointsPage.toastExportFail")));
    }
  };

  if (!currentEngId) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="rp-page-no-eng">
        {t("reviewPointsPage.noEngagement")}
      </div>
    );
  }

  const CARDS = [
    { key: "total", label: t("reviewPointsPage.summary.total"), color: "bg-slate-100" },
    { key: "pending", label: t("reviewPointsPage.summary.pending"), color: "bg-red-100" },
    { key: "in_progress", label: t("reviewPointsPage.summary.in_progress"), color: "bg-orange-100" },
    { key: "resolved", label: t("reviewPointsPage.summary.resolved"), color: "bg-blue-100" },
    { key: "cleared", label: t("reviewPointsPage.summary.cleared"), color: "bg-emerald-100" },
    { key: "overdue", label: t("reviewPointsPage.summary.overdue"), color: "bg-amber-100" },
  ];

  return (
    <ReviewCountsProvider engagementId={currentEngId}>
    <div className="space-y-4" data-testid="rp-dashboard-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="rp-dashboard-title">{t("reviewPointsPage.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("reviewPointsPage.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="rp-dashboard-refresh">
            <RefreshCw className="me-1 h-3 w-3" /> {t("reviewPointsPage.refreshBtn")}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline"
                      data-testid="rp-export-trigger">
                <Download className="me-1 h-3 w-3" /> {t("reviewPointsPage.exportBtn")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => doExport("csv")}
                                  data-testid="rp-export-csv">{t("reviewPointsPage.exportCsv")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport("xlsx")}
                                  data-testid="rp-export-xlsx">{t("reviewPointsPage.exportXlsx")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport("pdf")}
                                  data-testid="rp-export-pdf">{t("reviewPointsPage.exportPdf")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {CARDS.map((c) => (
          <Card key={c.key} className={`border ${c.color}`}
                data-testid={`rp-summary-${c.key}`}>
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-700">{c.label}</div>
              <div className="mt-0.5 text-2xl font-semibold tabular-nums">
                {summary[c.key] ?? 0}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-2 p-3">
          <div className="min-w-[220px] flex-1">
            <div className="relative">
              <Search className="pointer-events-none absolute start-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                      placeholder={t("reviewPointsPage.filterQPh")}
                      className="h-8 ps-6 text-xs"
                      data-testid="rp-filter-q" />
            </div>
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="rp-filter-status">
              <SelectValue placeholder={t("reviewPointsPage.statusPh")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("reviewPointsPage.allStatuses")}</SelectItem>
              <SelectItem value="Pending">{t("reviewPointsPage.status.Pending")}</SelectItem>
              <SelectItem value="In Progress">{t("reviewPointsPage.status.In Progress")}</SelectItem>
              <SelectItem value="Resolved">{t("reviewPointsPage.status.Resolved")}</SelectItem>
              <SelectItem value="Cleared">{t("reviewPointsPage.status.Cleared")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="rp-filter-role">
              <SelectValue placeholder={t("reviewPointsPage.rolePh")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("reviewPointsPage.allRoles")}</SelectItem>
              {uniqueRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="rp-filter-priority">
              <SelectValue placeholder={t("reviewPointsPage.priorityPh")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("reviewPointsPage.allPriorities")}</SelectItem>
              <SelectItem value="Critical">{t("reviewPointsPage.priority.Critical")}</SelectItem>
              <SelectItem value="High">{t("reviewPointsPage.priority.High")}</SelectItem>
              <SelectItem value="Medium">{t("reviewPointsPage.priority.Medium")}</SelectItem>
              <SelectItem value="Low">{t("reviewPointsPage.priority.Low")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={targetType} onValueChange={setTargetType}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="rp-filter-target">
              <SelectValue placeholder={t("reviewPointsPage.targetPh")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("reviewPointsPage.allTargets")}</SelectItem>
              {Object.entries(TARGET_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="table-sticky-box overflow-x-auto min-w-0">
          <table className="text-sm"
                 style={{ tableLayout: "fixed",
                          width: rpCols.colWidths.reduce((a,b)=>a+b,0) }}>
            <ResetCaption cols={rpCols} />
            <colgroup>
              {rpCols.colWidths.map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
            <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground dark:bg-slate-900/40">
              <tr>
                <ResizableTh index={0} onResizeStart={rpCols.onResizeStart} className="px-3 py-2 text-start">{t("reviewPointsPage.colReviewPoint")}</ResizableTh>
                <ResizableTh index={1} onResizeStart={rpCols.onResizeStart} className="px-3 py-2 text-start">{t("reviewPointsPage.colTarget")}</ResizableTh>
                <ResizableTh index={2} onResizeStart={rpCols.onResizeStart} className="px-3 py-2 text-start">{t("reviewPointsPage.colReference")}</ResizableTh>
                <ResizableTh index={3} onResizeStart={rpCols.onResizeStart} className="px-3 py-2 text-start">{t("reviewPointsPage.colRaisedBy")}</ResizableTh>
                <ResizableTh index={4} onResizeStart={rpCols.onResizeStart} className="px-3 py-2 text-start">{t("reviewPointsPage.colRole")}</ResizableTh>
                <ResizableTh index={5} onResizeStart={rpCols.onResizeStart} className="px-3 py-2 text-start">{t("reviewPointsPage.colStatus")}</ResizableTh>
                <ResizableTh index={6} onResizeStart={rpCols.onResizeStart} className="px-3 py-2 text-start">{t("reviewPointsPage.colPriority")}</ResizableTh>
                <ResizableTh index={7} onResizeStart={rpCols.onResizeStart} className="px-3 py-2 text-start">{t("reviewPointsPage.colRaised")}</ResizableTh>
                <ResizableTh index={8} onResizeStart={rpCols.onResizeStart} className="px-3 py-2 text-start">{t("reviewPointsPage.colCleared")}</ResizableTh>
                <ResizableTh index={9} onResizeStart={rpCols.onResizeStart} className="px-3 py-2 text-center">{t("reviewPointsPage.colOpen")}</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="p-6 text-center text-xs text-muted-foreground">{t("reviewPointsPage.loading")}</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-xs text-muted-foreground">
                    {t("reviewPointsPage.noMatch")}
                  </td>
                </tr>
              )}
              {!loading && filtered.map((r) => (
                <tr key={r.id} onClick={() => setOpenRow(r)}
                    className="border-b hover:bg-slate-50 cursor-pointer dark:hover:bg-slate-900/40"
                    data-testid={`rp-row-${r.id}`}>
                  <td className="px-3 py-2 max-w-[280px] break-words">{r.review_point_text}</td>
                  <td className="px-3 py-2 text-xs">{TARGET_LABEL[r.target_type] || r.target_type}</td>
                  <td className="px-3 py-2 text-xs max-w-[220px] break-words"
                        title={r.target_label || r.target_id}
                        data-testid={`rp-ref-${r.id}`}>
                    {r.target_label || r.target_id}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.raised_by_name}</td>
                  <td className="px-3 py-2 text-xs">{r.raised_by_role}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${STATUS_COLOR[r.status] || ""}`}>{t(`reviewPointsPage.status.${r.status}`) || r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{t(`reviewPointsPage.priority.${r.priority}`) || r.priority}</td>
                  <td className="px-3 py-2 text-xs tabular-nums">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-xs tabular-nums">{fmtDate(r.cleared_at)}</td>
                  <td className="px-3 py-2 text-center">
                    {r.target_href && (
                      <button type="button"
                                onClick={(e) => { e.stopPropagation(); navigate(r.target_href); }}
                                title={t("reviewPointsPage.openLabel").replace("{label}", r.target_label || t("reviewPointsPage.openFallback"))}
                                className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800"
                                data-testid={`rp-open-source-${r.id}`}>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>

      {openRow && (
        <ReviewPointsDrawer
          engagementId={currentEngId}
          targetType={openRow.target_type}
          targetId={openRow.target_id}
          vouchingSheetId={openRow.vouching_sheet_id}
          targetLabel={openRow.target_label
                          || `${TARGET_LABEL[openRow.target_type] || openRow.target_type} · ${(openRow.target_id || "").slice(0, 8)}…`}
          onClose={async () => { setOpenRow(null); await load(); }} />
      )}
    </div>
    </ReviewCountsProvider>
  );
}
